/**
 * UserCouponListContainer
 * --
 */
import React, { useState, useEffect, useMemo } from 'react';
import MessageAlert from 'utils';
import UserCouponListPresenter from './UserCouponListPresenter';
import dayjs from 'dayjs';
import { useGetUsers } from 'hooks/UserHooks';
import { useGetCoupons } from 'hooks/CouponHooks';
import {
  useBulkUpdateCouponUser,
  useGetCouponUser,
  useGetCouponUsers,
} from 'hooks/CouponUserHooks';
const INITIAL_VALUE = {
  type: '',
  start_date: dayjs().subtract(1, 'months').startOf('day'),
  end_date: dayjs().endOf('day'),
  value: 30,
  searchKey: '',
  searchValue: '',
  update_date: dayjs(),
};
/**----------------------------------------
 * [Component] 총관리자: 회원관리 -> 쿠폰발급내역
 * ----------------------------------------
 */
const UserCouponListContainer = ({ permission }) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  const [currentCoupon, setCurrentCoupon] = useState(null);
  const [period, setPeriod] = useState({
    start_date: dayjs().subtract(1, 'months').startOf('day').unix(),
    end_date: dayjs().endOf('day').unix(),
  });
  const [inputValue, setInputValue] = React.useState(INITIAL_VALUE);
  const [selectedList, setSelectedList] = useState([]);
  const [buttonType, setButtonType] = React.useState(null);
  const [modalData, setModalData] = React.useState(null);
  const confirm = (msg = '') => window.confirm(msg);

  /* ===== MUTATE ===== */
  const { data: MenuCouponList } = useGetCoupons(
    {
      type: 'MENU',
    },
    {
      select: (res) => {
        const { status, data } = res;
        if (status !== 200) {
          return [];
        }

        return data;
      },
      initialData: [],
    }
  );
  const { data: ProductCouponList } = useGetCoupons(
    {
      type: 'PRODUCT',
    },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) {
          return [];
        }

        return data;
      },
      initialData: [],
    }
  );

  const { data: UserList } = useGetUsers(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) {
        return [];
      }

      return data;
    },
    initialData: [],
  });

  const { data: couponUserList } = useGetCouponUsers(
    {
      coupon_id: currentCoupon,
      start_date: period.start_date,
      end_date: period.end_date,
    },
    {
      select: (res) => {
        const { status, data } = res;
        if (status !== 200) {
          return [];
        }

        return data;
      },
      initialData: [],
      enabled: currentCoupon ? true : false,
    }
  );

  const { bulkUpdateCouponUser } = useBulkUpdateCouponUser();
  const { matchedCoupon, uniqueCouponList } = useMemo(() => {
    const uniqueCouponList = [
      ...new Map(
        [...MenuCouponList, ...ProductCouponList].map((coupon) => [
          coupon.coupon_id,
          coupon,
        ])
      ).values(),
    ];
    ////
    const newMenuCoupon = uniqueCouponList?.find(
      (coupon) => coupon?.coupon_id === currentCoupon
    );

    const groupedUsers = {};

    couponUserList?.forEach((coupon_user) => {
      const foundUser = UserList?.find(
        (user) => user?.user_id === coupon_user?.user_id
      );

      const userKey = foundUser?.user_id;

      if (!groupedUsers[userKey]) {
        groupedUsers[userKey] = {
          key: userKey,
          ...foundUser,
          user_type: foundUser?.user_name ? 'USER' : 'NONE',
          route: foundUser?.social_type || 'MEMBER',
          coupon_user: [],
        };
      }
      groupedUsers[userKey].coupon_user.push({
        key: coupon_user?.coupon_user_id,
        ...coupon_user,
      });
    });

    const matchedCoupon = {
      key: newMenuCoupon?.coupon_id,
      ...newMenuCoupon,
      user: Object.values(groupedUsers),
    };
    return { matchedCoupon, uniqueCouponList };
  }, [MenuCouponList, ProductCouponList, couponUserList, currentCoupon]);
  /////--------
  /////--------
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */

  /**
   *
   * @param {string} t
   * @param {string | number} v
   */
  const handleChange = (t, v) => {
    setInputValue((prev) => ({
      ...prev,
      [t]: v,
    }));
  };
  /**--------------------
   * 쿠폰 핸들러
   * --------------------
   * @param {object | array} data
   * @param {number} reserve
   */
  const handleCouponUserUpdate = async () => {
    setLoading(true);
    try {
      const msg =
        buttonType === 'use_date'
          ? `${selectedList?.length}건을 사용처리 하시겠습니까?`
          : `${selectedList?.length}건을 만료처리 하시겠습니까?`;

      if (!confirm(msg)) return;

      await bulkUpdateCouponUser(
        selectedList?.map((x) => ({
          coupon_user_id: x.coupon_user_id,
          [buttonType]: inputValue.update_date.unix(),
        }))
      );
      MessageAlert.success(
        `쿠폰 ${
          buttonType === 'use_date' ? '사용' : '만료'
        }처리 성공하였습니다!`
      );
      setModalData({
        ...modalData,
        coupon_user: modalData.coupon_user?.map((item) => {
          const findcoupon = selectedList?.find(
            (select) => select?.coupon_user_id === item.coupon_user_id
          );
          if (findcoupon) {
            return {
              ...item,
              [buttonType]: inputValue.update_date.unix(),
            };
          }
          return item;
        }),
      });
    } catch (err) {
      console.error('[handeleCouponUserUpdate] Error!! ', err);
      MessageAlert.error(
        `쿠폰 ${buttonType === 'USED' ? '사용' : '만료'}처리 성공하였습니다!`
      );
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  /**
   * 회원 선택 핸들러
   * --
   * @param {string} type
   * @param {object | array} obj
   */
  const handleSelect = (type, obj) => {
    const selectType = type.toLocaleUpperCase();
    switch (selectType) {
      case 'CHECKED': // 개별 선택
        setSelectedList((prev) => [...prev, obj]);
        return;
      case 'UNCHECKED': // 개별 선택 해제
        setSelectedList((prev) =>
          prev?.filter((select) => select?.key !== obj?.key)
        );
        return;
      case 'ALL-CHECKED': // 노출된 목록 전체 선택
        setSelectedList(obj);
        return;
      case 'ALL-UNCHECKED': // 노출된 목록 전체 선택 해제
        const removeList = selectedList?.filter((user) =>
          obj?.some((ob) => ob?.key === user?.key)
        );

        setSelectedList(removeList);

        return;
      case 'ALL': // 검색된 회원 전체 선택/해제
        const isAll = selectedList?.length === obj?.length;

        if (!isAll) {
          setSelectedList(obj);
        } else {
          setSelectedList([]);
        }
        return;
      default:
        return;
    }
  };
  /* ===== HOOKS ===== */
  // /* 초기 설정 & 업체변경 시 반영 */
  /* ===== RENDER ===== */
  return (
    <UserCouponListPresenter
      {...{
        /* States */

        couponList: uniqueCouponList,
        couponUserList: couponUserList,
        coupon: matchedCoupon,
        permission,
        loading,
        setCurrentCoupon,
        currentCoupon,
        period,
        setPeriod,
        onChange: handleChange,
        inputValue: inputValue,
        setInputValue: setInputValue,
        /* Events */
        setLoading,

        buttonType,
        setButtonType,
        onSelect: handleSelect,
        INITIAL_VALUE,
        onHandleCouponUserUpdate: handleCouponUserUpdate,
        modalData,
        setModalData,
        selectedList,
        setSelectedList,
      }}
    />
  );
};

/* DF */
UserCouponListContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
};
export default UserCouponListContainer;
